import { Link } from 'react-router-dom';
import {
    ShieldCheck,
    Lock,
    Eye,
    Trash2,
    MapPin,
    KeyRound,
    FileText,
    History,
    UserX,
    ScrollText,
    Server,
    Fingerprint,
    ArrowRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { PublicLayout } from './PublicLayout';

// Trust page — the honest, structured answer to
// "why should we trust you with our invoice data?"
//
// Every claim on this page must be true and verifiable in-app. If we change
// architecture, we update this page in the same PR.

const PROMISES = [
    {
        icon: Lock,
        title: 'Your data is encrypted end-to-end',
        body: 'TLS 1.2+ in transit, AES-256 at rest. Passwords are bcrypt-hashed — even we cannot read them. Backups are encrypted and rotated on a 30-day cycle.',
    },
    {
        icon: MapPin,
        title: 'Hosted in India',
        body: 'Primary database and file storage are hosted in India-region infrastructure. Your bills stay under Indian data-protection law (DPDP Act, 2023).',
    },
    {
        icon: Eye,
        title: 'Every support access is logged and visible to you',
        body: 'If our team ever opens your workspace for support, we record who, when, why, and whether sensitive fields were revealed. You see this list in Settings → Privacy → Access Log — no hidden peeks.',
    },
    {
        icon: UserX,
        title: 'Sensitive fields are masked by default',
        body: 'When support views your workspace, GSTIN and invoice amounts are automatically masked into ranges (e.g. "₹10k–50k"). Full unmasking requires an explicit written reason — also logged.',
    },
    {
        icon: Trash2,
        title: 'One-click delete. Zero questions.',
        body: 'Settings → Danger Zone → Delete My Data wipes every bill, vendor, invoice, and staff record we hold for you. Backups are purged within 30 days.',
    },
    {
        icon: KeyRound,
        title: 'Multi-tenant isolation, always',
        body: 'Every query in our backend is scoped to your workspace by design. Other users on Ease My Bill literally cannot see, search, or reference your data — enforced at the database layer.',
    },
    {
        icon: History,
        title: 'Immutable audit log for every account action',
        body: 'Logins, exports, deletions, password changes, and admin access are appended to a tamper-evident log inside your workspace. Auditors and CAs can review it any time.',
    },
    {
        icon: ScrollText,
        title: 'We never share, sell, or train on your data',
        body: 'No third-party advertising trackers. No AI model training. Our AI provider processes your bills under API terms that explicitly forbid training on your inputs.',
    },
];

const FAQ = [
    {
        q: 'Can Ease My Bill employees see my invoices?',
        a: 'Only under two conditions: (1) you explicitly enable support access, or (2) a support engineer opens your workspace with a documented reason — which is recorded and shown to you in Settings → Access Log. Amounts and GSTIN are masked into range buckets by default. Nothing else, no other path.',
    },
    {
        q: 'Where is my data stored?',
        a: 'MongoDB Atlas (India region) for structured data, GridFS-backed object store for original bill images/PDFs. Both encrypted at rest with AES-256. We prefer India-region infrastructure everywhere possible.',
    },
    {
        q: 'What happens if I stop using Ease My Bill?',
        a: 'Deactivate anytime — no cancellation fee. Your data remains accessible for 90 days so you can export it. After 90 days, it is permanently deleted from primary storage and rotates out of backups within another 30 days. Or use Delete My Data to nuke it immediately.',
    },
    {
        q: 'Do you train AI models on my invoices?',
        a: 'No. Our AI provider operates under API-tier terms that forbid training on customer inputs. We do not run any internal model training on tenant data.',
    },
    {
        q: 'Are you compliant with Indian data-protection law?',
        a: 'Yes — we follow the Digital Personal Data Protection Act, 2023. You have rights to access, correct, port, and delete your data. Grievance mailbox: support@easemybill.com (7-day SLA).',
    },
    {
        q: 'Can I get a signed DPA or NDA?',
        a: 'Yes — for annual plans and enterprise customers. Email support@easemybill.com with your entity details and we will send a countersigned Data Processing Agreement within 3 business days.',
    },
    {
        q: 'What about payment card data?',
        a: 'We never see it. Razorpay collects and stores payment instruments directly under RBI regulations. We only receive transaction IDs and settlement status.',
    },
    {
        q: 'What if there is a data breach?',
        a: 'DPDP Act mandates notification within 72 hours. Our incident-response process: contain, notify affected tenants by email, publish a post-mortem on our status page, and file a Data Protection Board report within the statutory window.',
    },
];

function PromiseCard({ icon: Icon, title, body }) {
    return (
        <Card className="border-border/60" data-testid={`trust-promise-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40)}`}>
            <CardContent className="p-5">
                <div className="flex items-start gap-4">
                    <div className="shrink-0 h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                        <Icon size={20} />
                    </div>
                    <div className="min-w-0">
                        <h3 className="font-heading text-base mb-1.5 leading-tight">{title}</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

export default function TrustPage() {
    return (
        <PublicLayout>
            <div data-testid="trust-page" className="pb-16">
                {/* Hero */}
                <section className="border-b border-border/60 bg-gradient-to-b from-primary/[0.05] to-transparent">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
                        <div className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-background/80 px-3 py-1 text-xs text-muted-foreground mb-6">
                            <ShieldCheck size={14} className="text-primary" />
                            <span>Trust &amp; Security</span>
                        </div>
                        <h1 className="font-heading text-3xl sm:text-5xl leading-tight mb-4">
                            Your invoices are yours.<br />
                            <span className="text-primary">We just help you make sense of them.</span>
                        </h1>
                        <p className="text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
                            Handing invoice data to an outside tool is a big ask. Here is exactly how
                            we protect it, who can see it, and how you stay in control at every step.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3 mt-8">
                            <Button asChild size="lg" data-testid="trust-cta-start">
                                <Link to="/login?tab=signup">Start 14-day free trial</Link>
                            </Button>
                            <Button asChild size="lg" variant="outline" data-testid="trust-cta-privacy">
                                <Link to="/privacy">Read the Privacy Policy <ArrowRight size={16} className="ml-1" /></Link>
                            </Button>
                        </div>
                    </div>
                </section>

                {/* Promises grid */}
                <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
                    <div className="max-w-2xl mb-10">
                        <div className="text-xs uppercase tracking-wider text-primary font-medium mb-2">Our promises</div>
                        <h2 className="font-heading text-2xl sm:text-3xl mb-3">Eight commitments, in plain English</h2>
                        <p className="text-sm text-muted-foreground">
                            Each of these is enforced by our architecture — not just a policy statement.
                            If any of these ever stop being true, we will notify every customer by email within 24 hours.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {PROMISES.map((p) => (
                            <PromiseCard key={p.title} {...p} />
                        ))}
                    </div>
                </section>

                {/* Architecture diagram (text-based) */}
                <section className="border-y border-border/60 bg-muted/30">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
                        <div className="text-xs uppercase tracking-wider text-primary font-medium mb-2">How we are built</div>
                        <h2 className="font-heading text-2xl sm:text-3xl mb-6">The path your bill takes</h2>
                        <ol className="space-y-5 text-sm">
                            <li className="flex gap-4">
                                <div className="shrink-0 h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-mono text-sm font-semibold">1</div>
                                <div>
                                    <div className="font-medium mb-1">You upload a bill (photo or PDF)</div>
                                    <div className="text-muted-foreground">Transferred over TLS 1.2+. We generate a per-file SHA-256 hash so duplicate uploads are detected without keeping copies.</div>
                                </div>
                            </li>
                            <li className="flex gap-4">
                                <div className="shrink-0 h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-mono text-sm font-semibold">2</div>
                                <div>
                                    <div className="font-medium mb-1">The file lands in your isolated tenant workspace</div>
                                    <div className="text-muted-foreground">Every backend query is scoped by <code className="text-xs bg-background px-1 rounded">owner_email</code>. There is no unscoped code path that reads bills across tenants — enforced by the ORM layer.</div>
                                </div>
                            </li>
                            <li className="flex gap-4">
                                <div className="shrink-0 h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-mono text-sm font-semibold">3</div>
                                <div>
                                    <div className="font-medium mb-1">OCR runs on our AI provider (API tier — no training)</div>
                                    <div className="text-muted-foreground">The image and a strict prompt are sent to our AI over a private API. The API terms explicitly prohibit training on this input. The response returns to us and never leaves your workspace.</div>
                                </div>
                            </li>
                            <li className="flex gap-4">
                                <div className="shrink-0 h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-mono text-sm font-semibold">4</div>
                                <div>
                                    <div className="font-medium mb-1">Extracted data stored in your workspace, encrypted at rest</div>
                                    <div className="text-muted-foreground">MongoDB Atlas (India region) with AES-256 disk encryption. Original bill images stored in GridFS with the same encryption.</div>
                                </div>
                            </li>
                            <li className="flex gap-4">
                                <div className="shrink-0 h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-mono text-sm font-semibold">5</div>
                                <div>
                                    <div className="font-medium mb-1">Only you and your invited team see it</div>
                                    <div className="text-muted-foreground">Support access is opt-in and audited. Every read by our team creates an entry in your Access Log (Settings → Privacy) with reason and timestamp.</div>
                                </div>
                            </li>
                        </ol>
                    </div>
                </section>

                {/* You control section */}
                <section className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
                    <div className="text-xs uppercase tracking-wider text-primary font-medium mb-2">You stay in control</div>
                    <h2 className="font-heading text-2xl sm:text-3xl mb-8">Four self-serve tools inside the app</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="p-5 rounded-lg border border-border/60 bg-background">
                            <History size={22} className="text-primary mb-3" />
                            <div className="font-medium mb-1">Access Log</div>
                            <div className="text-sm text-muted-foreground">See every time our support team viewed your workspace, with the exact reason.</div>
                        </div>
                        <div className="p-5 rounded-lg border border-border/60 bg-background">
                            <FileText size={22} className="text-primary mb-3" />
                            <div className="font-medium mb-1">Export Everything</div>
                            <div className="text-sm text-muted-foreground">Excel, Tally XML, JSON — one click, no format lock-in.</div>
                        </div>
                        <div className="p-5 rounded-lg border border-border/60 bg-background">
                            <Fingerprint size={22} className="text-primary mb-3" />
                            <div className="font-medium mb-1">2FA &amp; Session Control</div>
                            <div className="text-sm text-muted-foreground">Enable TOTP-based two-factor login and revoke sessions from any device.</div>
                        </div>
                        <div className="p-5 rounded-lg border border-border/60 bg-background">
                            <Trash2 size={22} className="text-primary mb-3" />
                            <div className="font-medium mb-1">Delete My Data</div>
                            <div className="text-sm text-muted-foreground">Wipe your entire workspace with a single confirmation. Backups purged within 30 days.</div>
                        </div>
                    </div>
                </section>

                {/* FAQ */}
                <section className="border-t border-border/60 bg-muted/30">
                    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
                        <div className="text-xs uppercase tracking-wider text-primary font-medium mb-2">Common questions</div>
                        <h2 className="font-heading text-2xl sm:text-3xl mb-8">Answers we hear most often</h2>
                        <div className="space-y-6">
                            {FAQ.map((f, i) => (
                                <div
                                    key={f.q}
                                    className="pb-6 border-b border-border/60 last:border-0"
                                    data-testid={`trust-faq-${i}`}
                                >
                                    <h3 className="font-medium text-base mb-2">{f.q}</h3>
                                    <p className="text-sm text-muted-foreground leading-relaxed">{f.a}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Contact / CTA */}
                <section className="max-w-3xl mx-auto px-4 sm:px-6 py-14 text-center">
                    <Server size={32} className="mx-auto text-primary mb-4" />
                    <h2 className="font-heading text-2xl sm:text-3xl mb-3">
                        Need a signed DPA or a security review?
                    </h2>
                    <p className="text-sm text-muted-foreground max-w-2xl mx-auto mb-6">
                        We happily provide Data Processing Agreements, custom security questionnaires,
                        and architecture walkthroughs for annual and enterprise customers. Write to us —
                        we usually respond the same day.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Button asChild size="lg" data-testid="trust-cta-contact">
                            <a href="mailto:support@easemybill.com?subject=Security%20review%20%2F%20DPA%20request">
                                Email support@easemybill.com
                            </a>
                        </Button>
                        <Button asChild size="lg" variant="outline" data-testid="trust-cta-contact-page">
                            <Link to="/contact">Contact form <ArrowRight size={16} className="ml-1" /></Link>
                        </Button>
                    </div>
                </section>
            </div>
        </PublicLayout>
    );
}
